// Floating edges for the Evidence Map.
//
// Why floating edges: claim nodes expose a SINGLE whole-node connection handle
// (see ClaimNode) so drag-to-connect is unambiguous and direction follows the
// drag (ConnectionMode.Loose). With one handle, the handle has no fixed
// position, so a normal edge would anchor at the node centre and bury the
// arrowhead under the node. A floating edge instead computes the point on each
// node's border facing the other node, keeping the line and its arrowhead on
// the perimeter regardless of relative position. Adapted from the xyflow
// "floating edges" example for @xyflow/react v12.

import {
  BaseEdge,
  getBezierPath,
  useInternalNode,
  Position,
  type EdgeProps,
  type InternalNode,
  type Node,
  type ConnectionLineComponentProps,
} from "@xyflow/react";

// Fallback dimensions if a node hasn't been measured yet. Without these, an
// unmeasured node (width/height undefined) collapses the intersection math to
// the node's corner/centre, which hides the arrowhead under the box. The
// fallbacks roughly match the claim card so the border point stays sane until
// the real measurement lands.
const FALLBACK_W = 190;
const FALLBACK_H = 88;

function nodeCenter(node: InternalNode<Node>) {
  return {
    x: node.internals.positionAbsolute.x + (node.measured.width || FALLBACK_W) / 2,
    y: node.internals.positionAbsolute.y + (node.measured.height || FALLBACK_H) / 2,
  };
}

// Point where the line from `node`'s centre toward `other`'s centre crosses
// `node`'s rectangular border.
function intersection(node: InternalNode<Node>, other: InternalNode<Node>) {
  const w = (node.measured.width || FALLBACK_W) / 2;
  const h = (node.measured.height || FALLBACK_H) / 2;
  const cx = node.internals.positionAbsolute.x + w;
  const cy = node.internals.positionAbsolute.y + h;
  const { x: ox, y: oy } = nodeCenter(other);

  const xx = (ox - cx) / (2 * w || 1) - (oy - cy) / (2 * h || 1);
  const yy = (ox - cx) / (2 * w || 1) + (oy - cy) / (2 * h || 1);
  const a = 1 / (Math.abs(xx) + Math.abs(yy) || 1);
  const bx = a * xx;
  const by = a * yy;
  return { x: w * (bx + by) + cx, y: h * (-bx + by) + cy };
}

function borderPosition(node: InternalNode<Node>, point: { x: number; y: number }): Position {
  const nx = node.internals.positionAbsolute.x;
  const ny = node.internals.positionAbsolute.y;
  const w = node.measured.width || FALLBACK_W;
  const px = Math.round(point.x);
  const py = Math.round(point.y);
  if (px <= Math.round(nx) + 1) return Position.Left;
  if (px >= Math.round(nx + w) - 1) return Position.Right;
  if (py <= Math.round(ny) + 1) return Position.Top;
  return Position.Bottom;
}

export function getEdgeParams(source: InternalNode<Node>, target: InternalNode<Node>) {
  const sp = intersection(source, target);
  const tp = intersection(target, source);
  return {
    sx: sp.x,
    sy: sp.y,
    tx: tp.x,
    ty: tp.y,
    sourcePos: borderPosition(source, sp),
    targetPos: borderPosition(target, tp),
  };
}

export function FloatingEdge({ id, source, target, markerEnd, style, data }: EdgeProps) {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  if (!sourceNode || !targetNode) return null;

  const { sx, sy, tx, ty, sourcePos, targetPos } = getEdgeParams(sourceNode, targetNode);
  const [path, labelX, labelY] = getBezierPath({
    sourceX: sx,
    sourceY: sy,
    sourcePosition: sourcePos,
    targetX: tx,
    targetY: ty,
    targetPosition: targetPos,
  });

  return (
    <BaseEdge
      id={id}
      path={path}
      markerEnd={markerEnd}
      style={style}
      interactionWidth={20}
      label={(data as { label?: string } | undefined)?.label}
      labelX={labelX}
      labelY={labelY}
      labelStyle={{ fontSize: 10 }}
      labelShowBg
    />
  );
}

export function FloatingConnectionLine({ toX, toY, fromNode }: ConnectionLineComponentProps) {
  if (!fromNode) return null;

  // A 1×1 "node" at the cursor lets us reuse the border-intersection math so the
  // in-progress line leaves the source node's edge cleanly.
  const cursor = {
    measured: { width: 1, height: 1 },
    internals: { positionAbsolute: { x: toX, y: toY } },
  } as unknown as InternalNode<Node>;
  const { sx, sy, sourcePos } = getEdgeParams(fromNode, cursor);
  const [path] = getBezierPath({
    sourceX: sx,
    sourceY: sy,
    sourcePosition: sourcePos,
    targetX: toX,
    targetY: toY,
    targetPosition: Position.Top,
  });

  return (
    <g>
      <path fill="none" stroke="#94a3b8" strokeWidth={1.5} d={path} />
      <circle cx={toX} cy={toY} r={3} fill="#94a3b8" stroke="#fff" strokeWidth={1} />
    </g>
  );
}

export const claimEdgeTypes = { floating: FloatingEdge };
